/**
 * The compliance notice (Compliance Agent phases B to D). Phase D (operator
 * order 2026-10-24) made the card a notice: a reference line, the subject,
 * then Matter, Basis, Requested action and Response, in the organization's
 * own brand color when it has set one, formal and in the third person.
 *
 * Responses: Acknowledge (opens the record, or previews and runs the proposed
 * action on a second Acknowledge, with Undo), Decline (hold for a period, or
 * do not issue again with a reason; "Not mine" sends the item to the
 * compliance officer), Refer to Compliance (the question goes with the notice
 * attached). The Record opens what the notice rests on: the signals, standing
 * guidance and the facts on record (a fact can be disputed).
 *
 * The ownership notice (kind ownership, to the compliance officer) opens a
 * list of items with no accountable person; naming one runs the reassign
 * action in the item's own app, reversible, and from then on only that
 * person is notified.
 */
import React, { useState } from 'react';
import { api } from './api.js';

const SEV = { past_due: { label: 'Past due', cls: 'text-rose-700 bg-rose-50 ring-rose-200' }, high: { label: 'Due soon', cls: 'text-orange-800 bg-orange-50 ring-orange-200' }, normal: { label: 'Open', cls: 'text-slate-600 bg-slate-50 ring-slate-200' } };
const DISMISS = [
  { key: 'already_handled', label: 'Already handled' },
  { key: 'not_mine', label: 'Not mine' },
  { key: 'wrong_date', label: 'Wrong date' },
  { key: 'not_now', label: 'Not now' },
];
// A date-only value (a DATE column) is a calendar day, not an instant: read at noon UTC so it never slips a day west of Greenwich.
const fmt = (d) => {
  if (!d) return '';
  const s = String(d);
  const dayOnly = /^\d{4}-\d{2}-\d{2}$/.test(s);
  const dt = dayOnly ? new Date(`${s}T12:00:00Z`) : new Date(d);
  return Number.isNaN(dt.getTime()) ? s : dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', ...(dayOnly ? { timeZone: 'UTC' } : {}) });
};
const refOf = (card) => `${card.ownership_card ? 'OWN' : card.guidance_card ? 'GDN' : 'CN'}-${String(card.id || 0).padStart(5, '0')}`;

function Btn({ children, onClick, kind = 'primary', disabled, small = false, accent }) {
  const base = small ? 'px-3 py-1 text-[12px]' : 'px-4 py-1.5 text-[13px]';
  const styles = {
    primary: 'text-white hover:opacity-90',
    secondary: 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50',
    quiet: 'bg-transparent text-slate-500 hover:text-slate-800',
  };
  const style = kind === 'primary' ? { background: accent || '#E5601F' } : undefined;
  return <button type="button" onClick={onClick} disabled={disabled} style={style} className={`${base} rounded-full font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${styles[kind]}`}>{children}</button>;
}

function Row({ label, children, strong = false }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[112px_1fr] gap-x-4 gap-y-0.5 py-2 border-t border-slate-100 first:border-0">
      <dt className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 pt-0.5">{label}</dt>
      <dd className={`${strong ? 'font-medium text-slate-900' : 'text-slate-700'} leading-relaxed`}>{children}</dd>
    </div>
  );
}

/** The ownership list: one line per item, a person to name, Assign, Undo; Keep for a declined item. */
function OwnershipList({ card, accent, onAllDone, initial = null }) {
  const [data, setData] = useState(initial);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  React.useEffect(() => {
    if (initial) return;
    let dead = false;
    api(`/cards/${card.id}/ownership`).then((d) => { if (!dead) setData(d?.success ? d : { items: [], people: {}, error: d?.message }); });
    return () => { dead = true; };
  }, [card.id, initial]);
  if (!data) return <p className="mt-3 text-slate-400 text-[13px]">Reading the items…</p>;
  const items = data.items || [];
  const people = Object.entries(data.people || {});
  const assign = async (it) => {
    const to = pick[it.signal_key];
    if (!to) { setMsg('Choose the person first.'); return; }
    setBusy(it.signal_key); setMsg(null);
    const d = await api('/actions/run', { method: 'POST', body: { key: 'reassign', signal_key: it.signal_key, card_id: card.id, params: { to_user_id: to } } });
    setBusy(null);
    if (!d?.success) { setMsg(d?.message || 'The assignment did not run.'); return; }
    setData((x) => ({ ...x, items: x.items.map((i) => (i.signal_key === it.signal_key ? { ...i, assigned: { action_id: d.action_id, to } } : i)) }));
  };
  const undo = async (it) => {
    setBusy(it.signal_key); setMsg(null);
    const d = await api(`/actions/${it.assigned.action_id}/reverse`, { method: 'POST' });
    setBusy(null);
    if (!d?.success) { setMsg(d?.message || 'It could not be undone.'); return; }
    setData((x) => ({ ...x, items: x.items.map((i) => (i.signal_key === it.signal_key ? { ...i, assigned: null } : i)) }));
  };
  const keep = async (it) => {
    setBusy(it.signal_key); setMsg(null);
    const d = await api(`/ownership/${it.flag_id}/keep`, { method: 'POST', body: {} });
    setBusy(null);
    if (!d?.success) { setMsg(d?.message || 'Could not record that.'); return; }
    setData((x) => ({ ...x, items: x.items.filter((i) => i.signal_key !== it.signal_key) }));
  };
  const remaining = items.filter((i) => !i.assigned).length;
  const kindText = (it) => (it.kind === 'ambiguous' ? `${(it.candidate_names || []).length} people named: ${(it.candidate_names || []).join(', ')}` : it.kind === 'declined' ? `Declined by ${it.declined_by_name || 'the person named'}` : 'No one named');
  return (
    <div className="mt-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-[13px] text-slate-700" data-ownership>
      <p className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 mb-2">Items to assign · {remaining} remaining</p>
      {data.error && <p className="text-rose-700">{data.error}</p>}
      {items.length === 0 && !data.error && <p className="text-slate-600">Every item now has an accountable person.</p>}
      <div className="divide-y divide-slate-200">
        {items.map((it) => (
          <div key={it.signal_key} className="py-2 flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[220px]">
              <p className="text-slate-900">{it.href ? <a href={it.href} className="hover:underline">{it.label}</a> : it.label} <span className="text-slate-500">· {it.app}{it.when ? ` · ${it.when}` : ''}</span></p>
              <p className="text-[12px] text-slate-500">{kindText(it)}</p>
            </div>
            {it.assigned ? (
              <span className="flex items-center gap-2 text-[12.5px] text-emerald-800">Assigned to {data.people?.[it.assigned.to] || 'the person named'} <Btn kind="secondary" small onClick={() => undo(it)} disabled={busy === it.signal_key}>Undo</Btn></span>
            ) : (
              <span className="flex items-center gap-2 flex-wrap">
                <select value={pick[it.signal_key] || ''} onChange={(e) => setPick((p) => ({ ...p, [it.signal_key]: e.target.value }))} className="px-2 py-1 rounded-lg ring-1 ring-slate-200 bg-white text-[12.5px] max-w-[220px]" aria-label={`Accountable person for ${it.label}`}>
                  <option value="">Choose the accountable person</option>
                  {(it.candidates || []).length > 0 && <optgroup label="Named on the item">{it.candidates.map((id, i) => <option key={id} value={id}>{it.candidate_names?.[i] || 'A colleague'}</option>)}</optgroup>}
                  <optgroup label="Everyone">{people.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</optgroup>
                </select>
                <Btn small accent={accent} onClick={() => assign(it)} disabled={busy === it.signal_key || !pick[it.signal_key]}>Assign</Btn>
                {it.kind === 'declined' && it.flag_id && <Btn kind="secondary" small onClick={() => keep(it)} disabled={busy === it.signal_key}>Keep with the person named</Btn>}
              </span>
            )}
          </div>
        ))}
      </div>
      {msg && <p className="mt-2 text-rose-700">{msg}</p>}
      <div className="mt-3 flex gap-2">
        <Btn accent={accent} onClick={() => onAllDone(items.filter((i) => i.assigned).length)} disabled={busy != null}>{remaining === 0 ? 'Close the notice' : 'Done for now'}</Btn>
      </div>
    </div>
  );
}

export default function AgentCard({ card, onDone, compact = false, initialWhy = null, initialPreview = null, initialOwnership = null, brand = null }) {
  const [mode, setMode] = useState(initialPreview ? 'preview' : initialOwnership ? 'ownership' : initialWhy ? 'why' : null); // null | 'no' | 'ask' | 'why' | 'preview' | 'ownership' | 'done'
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [why, setWhy] = useState(initialWhy);
  const [preview, setPreview] = useState(initialPreview);
  const [period, setPeriod] = useState(card.suggested_snooze || '7d');
  const [custom, setCustom] = useState('');
  const [reason, setReason] = useState(null);
  const [question, setQuestion] = useState('');
  const [result, setResult] = useState(null);
  const [disputing, setDisputing] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const sev = SEV[card.severity] || SEV.normal;
  const accent = brand?.color || '#E5601F';
  const ink = brand?.ink || '#0F172A';
  const chip = card.guidance_card ? { label: 'Guidance', cls: 'text-slate-600 bg-slate-50 ring-slate-200' } : card.ownership_card ? { label: 'Ownership', cls: 'text-orange-800 bg-orange-50 ring-orange-200' } : sev;

  const decide = async (body) => {
    setBusy(true); setMsg(null);
    const d = await api(`/cards/${card.id}/decide`, { method: 'POST', body });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not record that.'); return null; }
    return d;
  };
  const acknowledge = async () => {
    if (card.ownership_card) { setMode('ownership'); return; }
    if (card.action_key && !card.guidance_card) {
      setBusy(true); setMsg(null);
      const d = await api(`/cards/${card.id}/action/preview`, { method: 'POST', body: {} });
      setBusy(false);
      if (!d?.success) { setMsg(d?.message || 'The action cannot be prepared.'); return; }
      setPreview(d); setMode('preview');
      return;
    }
    const d = await decide({ decision: 'approved' });
    if (!d) return;
    setResult(d); setMode('done');
    if (!d.reconfirmed && !d.draft && d.href) { try { window.location.assign(d.href); } catch { /* ignore */ } }
    onDone?.(card, 'approved');
  };
  const ownershipDone = async (assigned) => {
    const d = await decide({ decision: 'approved' });
    if (!d) return;
    setResult({ ...d, ownership: true, assigned }); setMode('done'); onDone?.(card, 'approved');
  };
  const runAction = async () => {
    setBusy(true); setMsg(null);
    const d = await api(`/cards/${card.id}/action/run`, { method: 'POST', body: { key: preview?.key } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'The action did not run.'); return; }
    setResult({ action: d }); setMode('done'); onDone?.(card, 'approved');
  };
  const undo = async () => {
    if (!result?.action?.action_id) return;
    setBusy(true); setMsg(null);
    const d = await api(`/actions/${result.action.action_id}/reverse`, { method: 'POST' });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'It could not be undone.'); return; }
    setMsg('Reversed.'); setResult({ action: { ...result.action, reversed: true } });
  };
  const hold = async () => {
    const d = await decide({ decision: 'snoozed', period, custom_date: period === 'custom' ? custom : undefined });
    if (!d) return;
    setResult(d); setMode('done'); onDone?.(card, 'snoozed');
  };
  const dismiss = async () => {
    if (!reason) { setMsg('Choose a reason.'); return; }
    const d = await decide({ decision: 'dismissed', reason });
    if (!d) return;
    setResult(d); setMode('done'); onDone?.(card, 'dismissed');
  };
  const refer = async () => {
    setBusy(true); setMsg(null);
    const d = await api(`/cards/${card.id}/ask`, { method: 'POST', body: { question: question || 'Please advise on this matter.' } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not send the question.'); return; }
    setResult({ asked: true }); setMode('done'); onDone?.(card, 'asked');
  };
  const openRecord = async () => {
    if (mode === 'why') { setMode(null); return; }
    setMode('why');
    if (why) return;
    const d = await api(`/cards/${card.id}/why`);
    setWhy(d?.success ? d : { signals: [], guidance: [], memory: [], error: d?.message });
  };
  const dispute = async (id) => {
    if (!disputeReason.trim()) { setMsg('State why the fact is wrong.'); return; }
    setBusy(true); setMsg(null);
    const d = await api(`/memory/${id}/dispute`, { method: 'POST', body: { reason: disputeReason } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not dispute.'); return; }
    setWhy((w) => ({ ...w, memory: (w?.memory || []).filter((f) => f.id !== id) })); setDisputing(null); setDisputeReason(''); setMsg('The fact was removed and will not be written again.');
  };
  const openItem = async () => {
    await api(`/cards/${card.id}/events`, { method: 'POST', body: { kind: 'opened' } }).catch(() => {});
    if (card.href) { try { window.location.assign(card.href); } catch { /* ignore */ } }
  };
  const periodLabel = (k) => (card.periods || []).find((p) => p.key === k)?.label?.toLowerCase() || '';

  return (
    <article className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden text-left shadow-[0_8px_30px_-18px_rgba(15,23,42,0.25)]" style={{ borderTop: `3px solid ${accent}` }} data-card-id={card.id} data-notice>
      <div className={compact ? 'px-4 pt-3' : 'px-5 pt-4'}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500">Compliance notice · {refOf(card)}{card.shown_at ? ` · ${fmt(card.shown_at)}` : ''}</p>
          <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${chip.cls}`}>{chip.label}</span>
        </div>
        <h3 className={`${compact ? 'text-[14px]' : 'text-[15.5px]'} font-semibold leading-snug mt-1.5`} style={{ color: ink }}>{card.headline}</h3>
      </div>

      {mode !== 'done' && mode !== 'preview' && (
        <dl className={`${compact ? 'px-4 text-[13px]' : 'px-5 text-[13.5px]'} mt-2`}>
          <Row label="Matter">{card.noticed}</Row>
          <Row label="Basis">{card.understanding}</Row>
          <Row label="Requested action">{card.approach}</Row>
          <Row label="Response" strong>{card.question}</Row>
        </dl>
      )}

      <div className={compact ? 'px-4 pb-4' : 'px-5 pb-5'}>
        {mode === 'preview' && preview && (
          <div className="mt-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-[13px] text-slate-700" data-action-preview>
            <p className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 mb-1.5">Before it runs · {preview.label}</p>
            <dl className="space-y-1">
              {(preview.preview?.lines || []).map(([k, v]) => <div key={k} className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">{k}</dt><dd className="whitespace-pre-wrap text-slate-800">{v}</dd></div>)}
            </dl>
            {preview.preview?.note && <p className="mt-2 text-[12px] text-slate-500">{preview.preview.note}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <Btn accent={accent} onClick={runAction} disabled={busy}>Acknowledge and proceed</Btn>
              <Btn kind="quiet" onClick={() => setMode(null)} disabled={busy}>Back</Btn>
            </div>
          </div>
        )}

        {mode === 'ownership' && <OwnershipList card={card} accent={accent} onAllDone={ownershipDone} initial={initialOwnership} />}

        {mode === 'done' && (
          <div className="mt-3 text-[13px] text-slate-700 leading-relaxed">
            {result?.asked && <p>Referred to the Compliance Department. Their answer appears here and by email.</p>}
            {result?.reconfirmed && <p>Recorded. The guidance stands until {fmt(result.expires_at)}.</p>}
            {result?.decision === 'snoozed' && <p>Recorded. This notice is held until {fmt(result.snooze_until)} unless the record moves.</p>}
            {result?.decision === 'dismissed' && <p>{result.reason === 'not_mine' ? 'Recorded. The item goes to the compliance officer to name the accountable person; this notice will not return to you.' : 'Recorded. This notice will not be issued again unless the record changes.'}</p>}
            {result?.ownership && <p>Recorded. {result.assigned ? `${result.assigned} ${result.assigned === 1 ? 'item was' : 'items were'} assigned; from now on only the person named receives notices about ${result.assigned === 1 ? 'it' : 'them'}.` : 'No item was assigned; the items stay in the brief.'}</p>}
            {result?.action && (
              <div>
                <p>{result.action.reversed ? 'Reversed.' : `Done: ${result.action.label?.toLowerCase() || 'the action ran'}.`}</p>
                {!result.action.reversed && result.action.reversible && <div className="mt-2"><Btn kind="secondary" small onClick={undo} disabled={busy}>Undo</Btn></div>}
              </div>
            )}
            {result?.decision === 'approved' && !result.reconfirmed && !result.ownership && (
              <div>
                <p>{result.draft ? 'The note is ready to copy.' : 'Opening the record.'}</p>
                {result.draft && (
                  <div className="mt-2">
                    <pre className="whitespace-pre-wrap text-[12.5px] bg-slate-50 ring-1 ring-slate-200 rounded-xl p-3 text-slate-800">{result.draft}</pre>
                    <div className="mt-2 flex gap-2">
                      <Btn kind="secondary" small onClick={() => { try { navigator.clipboard?.writeText(result.draft); setMsg('Copied.'); } catch { setMsg('Select the text to copy it.'); } }}>Copy the note</Btn>
                      {card.href && <Btn kind="secondary" small onClick={openItem}>Open the record</Btn>}
                    </div>
                  </div>
                )}
              </div>
            )}
            {msg && <p className="mt-2 text-slate-500">{msg}</p>}
          </div>
        )}

        {mode === 'why' && (
          <div className="mt-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-[12.5px] text-slate-700">
            {!why ? <p className="text-slate-400">Reading the record…</p> : (
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 mb-1.5">The record · {why.engine === 'judge' ? `composed by the Judge, confidence ${Math.round((why.confidence || 0) * 100)}%` : 'composed from the set template'}{why.action_label ? ` · on acknowledgment: ${why.action_label.toLowerCase()}` : ''}</p>
                {why.error && <p className="text-rose-700">{why.error}</p>}
                <ul className="space-y-1">
                  {(why.signals || []).map((s) => (
                    <li key={s.signal_key} className="flex flex-wrap gap-x-2">
                      <span className="font-medium text-slate-800">{s.rule_title}</span>
                      <span>{s.label}</span>
                      {s.due_date && <span className="text-slate-500">due {fmt(s.due_date)}</span>}
                      <span className="text-slate-500">on record since {fmt(s.first_seen)}</span>
                      {s.resolved_at && <span className="text-emerald-700">resolved</span>}
                    </li>
                  ))}
                </ul>
                {(why.guidance || []).length > 0 && (
                  <div className="mt-2">
                    <p className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 mb-1">Standing guidance from the Compliance Department</p>
                    {why.guidance.map((g, i) => <p key={i} className="italic">“{g.text}” <span className="not-italic text-slate-500">({g.answered_by_role}{g.expires_at ? `, until ${fmt(g.expires_at)}` : ''})</span></p>)}
                  </div>
                )}
                {(why.memory || []).length > 0 && (
                  <div className="mt-2" data-memory>
                    <p className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 mb-1">Facts on record</p>
                    {why.memory.map((f) => (
                      <div key={f.id} className="py-1">
                        <p>{f.fact} <span className="text-slate-500">(from a {f.source_kind}, seen {fmt(f.last_observed_at)}, kept until {fmt(f.expires_at)})</span> {disputing !== f.id && <button type="button" className="hover:underline" style={{ color: accent }} onClick={() => setDisputing(f.id)}>Dispute</button>}</p>
                        {disputing === f.id && (
                          <div className="mt-1 flex flex-wrap gap-2 items-center">
                            <input value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} maxLength={500} placeholder="Why is this wrong?" className="flex-1 min-w-[200px] px-2 py-1 rounded-lg ring-1 ring-slate-200 bg-white text-[12.5px]" />
                            <Btn small accent={accent} onClick={() => dispute(f.id)} disabled={busy}>Remove it</Btn>
                            <Btn small kind="quiet" onClick={() => setDisputing(null)}>Back</Btn>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {msg && <p className="mt-2 text-slate-600">{msg}</p>}
              </div>
            )}
          </div>
        )}

        {mode === 'no' && (
          <div className="mt-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-[13px] text-slate-700">
            <p className="mb-2">Hold this notice for a period. The suggested period is {periodLabel(card.suggested_snooze) || '7 days'}.</p>
            <div className="flex flex-wrap gap-2 items-center">
              {(card.periods || []).map((p) => (
                <label key={p.key} className={`px-3 py-1 rounded-full ring-1 cursor-pointer ${period === p.key ? 'text-white ring-transparent' : 'bg-white ring-slate-200'}`} style={period === p.key ? { background: accent } : undefined}>
                  <input type="radio" name={`snooze-${card.id}`} className="sr-only" checked={period === p.key} onChange={() => setPeriod(p.key)} />{p.label}
                </label>
              ))}
              <label className={`px-3 py-1 rounded-full ring-1 cursor-pointer ${period === 'custom' ? 'text-white ring-transparent' : 'bg-white ring-slate-200'}`} style={period === 'custom' ? { background: accent } : undefined}>
                <input type="radio" name={`snooze-${card.id}`} className="sr-only" checked={period === 'custom'} onChange={() => setPeriod('custom')} />A date
              </label>
              {period === 'custom' && <input type="date" value={custom} onChange={(e) => setCustom(e.target.value)} className="px-2 py-1 rounded-lg ring-1 ring-slate-200 bg-white text-[13px]" aria-label="Hold until" />}
            </div>
            <p className="mt-3 mb-1.5 text-slate-500">Or state that this notice should not be issued again, and why.</p>
            <div className="flex flex-wrap gap-2">
              {DISMISS.map((d) => (
                <label key={d.key} className={`px-3 py-1 rounded-full ring-1 cursor-pointer ${reason === d.key ? 'bg-slate-700 text-white ring-slate-700' : 'bg-white ring-slate-200'}`}>
                  <input type="radio" name={`dismiss-${card.id}`} className="sr-only" checked={reason === d.key} onChange={() => setReason(d.key)} />{d.label}
                </label>
              ))}
            </div>
            {reason === 'not_mine' && <p className="mt-2 text-[12.5px] text-slate-500">The item goes to the compliance officer, who names the accountable person. Only that person is notified from then on.</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <Btn accent={accent} onClick={hold} disabled={busy || (period === 'custom' && !custom)}>Hold{period !== 'custom' ? ` ${periodLabel(period)}` : ''}</Btn>
              <Btn kind="secondary" onClick={dismiss} disabled={busy || !reason}>Do not issue again</Btn>
              <Btn kind="quiet" onClick={() => setMode(null)} disabled={busy}>Back</Btn>
            </div>
          </div>
        )}

        {mode === 'ask' && (
          <div className="mt-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-[13px] text-slate-700">
            <p className="mb-2">The matter is referred to the Compliance Department with this notice attached. Their answer appears here and by email.</p>
            <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={3} maxLength={4000} placeholder="State the question for the Compliance Department." className="w-full px-3 py-2 rounded-xl ring-1 ring-slate-200 bg-white text-[13px]" />
            <div className="mt-2 flex gap-2">
              <Btn accent={accent} onClick={refer} disabled={busy}>Refer</Btn>
              <Btn kind="quiet" onClick={() => setMode(null)} disabled={busy}>Back</Btn>
            </div>
          </div>
        )}

        {mode !== 'done' && mode !== 'no' && mode !== 'ask' && mode !== 'preview' && mode !== 'ownership' && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Btn accent={accent} onClick={acknowledge} disabled={busy}>{card.ownership_card ? 'Assign' : 'Acknowledge'}</Btn>
            <Btn kind="secondary" onClick={() => setMode('no')} disabled={busy}>Decline</Btn>
            {!card.guidance_card && !card.ownership_card && <Btn kind="secondary" onClick={() => setMode('ask')} disabled={busy}>Refer to Compliance</Btn>}
            <span className="flex-1" />
            <Btn kind="quiet" small onClick={openRecord}>{mode === 'why' ? 'Hide the record' : 'Record'}</Btn>
            {card.href && !card.guidance_card && !card.ownership_card && <Btn kind="quiet" small onClick={openItem}>Open the record</Btn>}
          </div>
        )}
        {msg && mode !== 'done' && mode !== 'why' && mode !== 'ownership' && <p className="mt-2 text-[12.5px] text-rose-700">{msg}</p>}
      </div>
    </article>
  );
}
